import { colors } from '../../../theme/tokens/colors';
import { chat as defaultAccent } from '../../../theme/tokens/semantic';
import { radius, radiusPx } from '../../../theme/tokens/radius';
import { shadows as defaultShadows } from '../../../theme/tokens/shadows';

export function createOnboardingSx(tokenOverrides = {}) {
  const accent = tokenOverrides.accent ?? defaultAccent;
  const shadows = tokenOverrides.shadows ?? defaultShadows;
  const controlRadius = radiusPx.md;

  const sharedInputRoot = {
    borderRadius: controlRadius,
    fontSize: '0.875rem',
    '& fieldset': { borderColor: accent.divider ?? colors.grey[300] },
    '&:hover fieldset': { borderColor: colors.grey[400] },
    '&.Mui-focused fieldset': {
      borderColor: accent.primary,
      borderWidth: 1,
    },
  };

  return {
    pageRoot: {
      maxWidth: 1200,
      mx: 'auto',
      pb: 4,
    },
    layoutGrid: {
      spacing: 3,
    },
    loadingBox: {
      display: 'flex',
      justifyContent: 'center',
      py: 8,
    },
    alert: {
      mb: 2,
    },
    progressHeader: {
      mb: 2,
    },
    progressTitleRow: {
      display: 'flex',
      justifyContent: 'space-between',
      mb: 0.5,
    },
    progressTitle: {
      fontWeight: 600,
    },
    progressPercent: {
      color: accent.textSecondary ?? colors.grey[600],
    },
    progressBar: {
      height: 8,
      borderRadius: 1,
      bgcolor: colors.grey[200],
      '& .MuiLinearProgress-bar': {
        bgcolor: accent.primary,
        borderRadius: 1,
      },
    },
    stepper: {
      width: '100%',
    },
    stepperVertical: {
      minWidth: 220,
    },
    stepperHorizontal: {
      overflowX: 'auto',
      pb: 1,
    },
    stepButton: {
      textAlign: 'left',
    },
    stepPanel: {
      p: { xs: 2, md: 3 },
      border: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
      borderRadius: `${radius.lg}px`,
      bgcolor: colors.white,
    },
    stepPanelHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 2,
    },
    stepPanelTitle: {
      fontWeight: 600,
    },
    stepPanelHelp: {
      mt: 3,
      pt: 2,
      borderTop: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
      bgcolor: accent.primaryMuted ?? colors.grey[50],
      mx: -3,
      mb: -3,
      px: 3,
      pb: 2,
      borderRadius: `0 0 ${radius.lg}px ${radius.lg}px`,
    },
    stepPanelHelpLabel: {
      fontWeight: 600,
      color: accent.textSecondary ?? colors.grey[600],
    },
    stepPanelHelpText: {
      mt: 0.5,
      color: accent.textSecondary ?? colors.grey[600],
    },
    sectionIcon: {
      width: 40,
      height: 40,
      borderRadius: `${radius.md}px`,
      bgcolor: accent.primary,
      color: accent.primaryContrast ?? colors.white,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    accentText: {
      color: accent.primary,
    },
    requiredMark: {
      color: 'error.main',
    },
    sectionSubheading: {
      mb: 1,
      display: 'block',
      fontWeight: 600,
      color: accent.textSecondary ?? colors.grey[600],
    },
    subdomainPreviewBox: {
      mt: 1.5,
      px: 2,
      py: 1.25,
      bgcolor: colors.grey[50],
      borderColor: accent.divider ?? colors.grey[300],
      borderStyle: 'dashed',
    },
    uploadIcon: {
      fontSize: 40,
      color: accent.textMuted ?? colors.grey[500],
      mb: 1,
    },
    inputAdornmentIcon: {
      color: accent.primary,
    },
    logoUploadBox: {
      p: 3,
      textAlign: 'center',
      border: '2px dashed',
      borderColor: accent.divider ?? colors.grey[300],
      borderRadius: `${radius.md}px`,
      bgcolor: colors.grey[50],
    },
    logoUploadBoxActive: {
      borderColor: accent.primary,
      bgcolor: accent.primaryMuted ?? colors.grey[50],
    },
    wizardFooter: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      justifyContent: 'space-between',
      mt: 3,
    },
    wizardFooterGroup: {
      display: 'flex',
      gap: 1,
      alignItems: 'center',
    },
    backButton: {
      textTransform: 'none',
      borderRadius: controlRadius,
      borderColor: accent.divider ?? colors.grey[300],
      color: accent.textPrimary ?? colors.grey[800],
    },
    continueButton: {
      textTransform: 'none',
      fontWeight: 600,
      borderRadius: controlRadius,
      bgcolor: accent.primary,
      boxShadow: 'none',
      '&:hover': {
        bgcolor: accent.primaryDark,
        boxShadow: shadows.md,
      },
    },
    completeButton: {
      textTransform: 'none',
      fontWeight: 600,
      borderRadius: controlRadius,
    },
    textButton: {
      textTransform: 'none',
      color: accent.textSecondary ?? colors.grey[600],
    },
    field: {
      '& .MuiOutlinedInput-root': sharedInputRoot,
    },
    statusChip: {
      fontWeight: 600,
      fontSize: '0.75rem',
    },
    permissionPanel: {
      border: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
      borderRadius: `${radius.md}px`,
      overflow: 'hidden',
      bgcolor: colors.white,
    },
    permissionModuleSummary: {
      px: 2,
      py: 1.25,
      minHeight: 48,
      '& .MuiAccordionSummary-content': {
        my: 0.5,
        alignItems: 'center',
      },
    },
    permissionModuleDetails: {
      px: 2,
      pt: 0,
      pb: 1.5,
      bgcolor: colors.grey[50],
    },
    permissionObjectCard: {
      p: 2,
      mb: 1.5,
      border: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
      borderRadius: `${radius.md}px`,
      bgcolor: colors.white,
    },
    permissionObjectHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 1.5,
      gap: 1,
      flexWrap: 'wrap',
    },
    permissionActionGrid: {
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
      gap: 1,
    },
    permissionActionItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      px: 1.25,
      py: 0.75,
      borderRadius: `${radius.sm}px`,
      border: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
      bgcolor: colors.white,
    },
    permissionRow: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 2,
      py: 1,
      borderBottom: '1px solid',
      borderColor: accent.divider ?? colors.grey[200],
      '&:last-child': { borderBottom: 'none' },
    },
    permissionCountChip: {
      fontWeight: 600,
      fontSize: '0.75rem',
      bgcolor: accent.primaryMuted ?? colors.grey[100],
      color: accent.textSecondary ?? colors.grey[700],
    },
  };
}
